import { useState, useRef, useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import Button, { ButtonType } from "../../components/Button";
import "./Packages.scss";
import MessageModal, { MessageModalType } from "../../components/modals/MessageModal";
import { PackageLevelType } from "../../features/Customers/PackageLevelType";
import ViewPackages from "./ViewPackages";
import { ResellerPackageType } from "../../features/Resellers/PackageType";

export const SelectPackagesMode = Object.freeze({
	Customer: "customer",
	Reseller: "reseller"
});

const SelectPackages = ({
	packageData,
	onChangeInput,
	selectedPackages = [],
	setAddedPackage,
	isValid,
	mode = SelectPackagesMode.Customer,
	disableUserModification
}) => {
	const allPackages = packageData || [];

	const [currentPackages, setCurrentPackages] = useState(Array.isArray(selectedPackages) ? selectedPackages : []);
	const [pendingPackage, setPendingPackage] = useState(null);
	const [PUP, setPUP] = useState(null);
	const [seatQuantity, setSeatQuantity] = useState(null);
	const [selectedLevel, setSelectedLevel] = useState(null);
	const [name, setName] = useState("");
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [packageToRemove, setPackageToRemove] = useState(false);
	const [showPackageList, setShowPackageList] = useState(false);

	const initialPackagesRef = useRef(JSON.stringify(currentPackages));
	const addPackageRef = useRef(null);

	const availablePackages = allPackages
		.filter(pkg => !currentPackages.some(cp =>
			mode === SelectPackagesMode.Customer
				? cp.PackageId === pkg.PackageId
				: cp.ParentId === pkg.ParentId
		))
		.map(pkg => ({
			...pkg,
			Name: pkg.PackageName,
			Price: mode === SelectPackagesMode.Customer ? null : pkg.Price,
			OnboardingPrice: mode === SelectPackagesMode.Customer ? null : pkg.OnboardingPrice,
			UpsellPrice: mode === SelectPackagesMode.Customer ? null : pkg.UpsellPrice,
			BronzePrice: pkg.BronzePrice,
			SilverPrice: pkg.SilverPrice,
			GoldPrice: pkg.GoldPrice,
			PlatinumPrice: pkg.PlatinumPrice
		}));

	useEffect(() => {
		if (typeof setAddedPackage === "function") {
			setAddedPackage(currentPackages.length > 0);
		}
		if (typeof isValid === "function") {
			isValid(initialPackagesRef.current !== JSON.stringify(currentPackages));
		}
	}, [currentPackages, isValid]);

	useEffect(() => {
		if (typeof onChangeInput === "function") {
			if (mode === SelectPackagesMode.Customer) {
				onChangeInput({ currentCustomerPackages: currentPackages });
			} else if (mode === SelectPackagesMode.Reseller) {
				onChangeInput({ packages: currentPackages });
			}
		}
	}, [currentPackages]);

	const handleSelect = (pkg) => {
		if (mode === SelectPackagesMode.Reseller) {
			setCurrentPackages(prev => [...prev, pkg]);
			setShowPackageList(false);
			if (typeof setAddedPackage === "function") setAddedPackage(true);
			if (typeof isValid === "function") isValid(true);
			if (typeof onChangeInput === "function") {
				onChangeInput({ packages: [...currentPackages, pkg] });
			}
			return;
		}
		setPendingPackage(pkg);
		setPUP(pkg.PUP);
		setSeatQuantity(null);
		setSelectedLevel(null);
		setName(pkg.Name);
		setTimeout(() => {
			addPackageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
		}, 100);
	};

	const handleCloseSelect = () => setShowPackageList(false);
	const handleCloseAddPackage = () => {
		setShowPackageList(false);
		setPendingPackage(null);
	};

	const handleConfirmDelete = (pkgOrId) => {
		setPackageToRemove(pkgOrId);
		setConfirmDelete(true);
	};

	const handleRemovePackage = (yes) => {
		if (yes) {
			setCurrentPackages(prev => {
				const updated = prev.filter(pkg => pkg.Id !== packageToRemove);

				if (mode === SelectPackagesMode.Reseller && typeof onChangeInput === "function") {
					onChangeInput({ packages: updated });
				} else if (typeof onChangeInput === "function") {
					onChangeInput({ currentCustomerPackages: updated });
				}

				return updated;
			});
		}
		setConfirmDelete(false);
	};

	const handleAddPendingPackage = () => {
		if (!pendingPackage) {
			return;
		}

		if (mode === SelectPackagesMode.Customer) {
			const finalSeatCount = pendingPackage.PackageType === ResellerPackageType.SingleUnlimitedUsers
				? 99999
				: seatQuantity;

			const newPackage = {
				Id: pendingPackage.ParentId,
				ParentId: pendingPackage.PackageId,
				PackageId: pendingPackage.PackageId,
				Name: pendingPackage.Name,
				PackageName: pendingPackage.PackageName,
				ResellerName: pendingPackage.ResellerName,
				Seats: finalSeatCount,
				SeatQuantity: finalSeatCount,
				SeatBlockSize: pendingPackage.SeatBlockSize,
				Level: selectedLevel?.CustomerPackageLevel ?? -1,
				LevelName: selectedLevel?.Name ?? null,
				LevelId: selectedLevel?.Id ?? -1,
				CanDelete: false,
				PUP: PUP,
				PackageType: pendingPackage.PackageType
			};

			setCurrentPackages(prev => [...prev, newPackage]);
		} else {
			setCurrentPackages(prev => [...prev, pendingPackage]);
		}

		setPendingPackage(null);
		setShowPackageList(false);
		setPUP(null);
		setSeatQuantity(null);
		setSelectedLevel(null);
		setName("");
	};

	const handleUpdatePackageName = (index, newName) => {
		setCurrentPackages(prev =>
			prev.map((pkg, i) => i === index ? { ...pkg, Name: newName } : pkg)
		);
	};

	const fieldVisibility = (pkg) => {
		if (!pkg) {
			return { PUP: false, seatQuantity: false, level: false };
		}
		const isSeatQuantityRequired = pkg.PackageType !== ResellerPackageType.SingleUnlimitedUsers;
		const hasLevelPrices = pkg.BronzePrice || pkg.SilverPrice || pkg.GoldPrice || pkg.PlatinumPrice;

		return {
			pup: pkg.PUP,
			seatQuantity: isSeatQuantityRequired,
			level: hasLevelPrices
		};
	};

	const levelsAvailable = (pkg) => {
		const levelMap = [
			[PackageLevelType.Bronze, pkg.BronzePrice],
			[PackageLevelType.Silver, pkg.SilverPrice],
			[PackageLevelType.Gold, pkg.GoldPrice],
			[PackageLevelType.Platinum, pkg.PlatinumPrice]
		];
		return levelMap.filter(([_, price]) => price).map(([level]) => level);
	};

	const canAdd =
		pendingPackage &&
		(!fieldVisibility(pendingPackage).seatQuantity || seatQuantity) &&
		(!fieldVisibility(pendingPackage).level || selectedLevel !== null) &&
		name.trim().length > 0;

	return (
		<div className="select-package">
			{!showPackageList && !pendingPackage && !disableUserModification && (
				<div className="add-button">
					<h4>Add Package</h4>
					<Button
						label="Add New Package"
						type={ButtonType.Primary}
						onClick={() => setShowPackageList(true)}
						className="add-button"
					/>
				</div>
			)}

			{showPackageList && !pendingPackage && (
				availablePackages.length > 0 ? (
					<ViewPackages
						selectedPackages={availablePackages}
						onSelectPackage={handleSelect}
						altTitle="Available Packages to Add"
						onClose={handleCloseSelect}
						hidePrices={mode === SelectPackagesMode.Customer}
					/>
				) : (
					<div className="no-packages-message">
						<p>All available packages have been assigned.</p>
						<Button label="Back" type={ButtonType.Secondary} onClick={handleCloseSelect} />
					</div>
				)
			)}

			{pendingPackage && mode !== SelectPackagesMode.Reseller && (
				<>
					<h4 ref={addPackageRef}>Adding {name} package</h4>
					<div className="add-package-grid">
						{fieldVisibility(pendingPackage).seatQuantity && (
							<div className="form-cell">
								{PUP ? <label>Seat Quantity</label> : <label>Package Quantity</label>}
								<InputNumber
									value={seatQuantity}
									onValueChange={(e) => setSeatQuantity(e.value)}
									min={1}
								/>
								{!PUP &&
									pendingPackage.PackageType !== ResellerPackageType.SingleUnlimitedUsers &&
									seatQuantity && (
										<p className="seats-info">
											{seatQuantity} × {pendingPackage.SeatBlockSize} (Seats Per Package) =
											{" "}
											<strong>{seatQuantity * pendingPackage.SeatBlockSize} Seats</strong>
										</p>
									)}
							</div>
						)}

						{fieldVisibility(pendingPackage).level && (
							<div className="form-cell">
								<label>Select Level</label>
								<div className="tier-options">
									{levelsAvailable(pendingPackage).map((level) => (
										<div key={level.Id} className="tier-radio">
											<input
												type="radio"
												id={`tier-${level.Id}`}
												name="tier"
												value={level.CustomerPackageLevel}
												checked={selectedLevel?.Id === level.Id}
												onChange={() => setSelectedLevel(level)}
											/>
											<label htmlFor={`tier-${level.Id}`}>{level.Name}</label>
										</div>
									))}
								</div>
							</div>
						)}
					</div>

					<div className="add-package-buttons">
						<Button label="Cancel" type={ButtonType.Secondary} onClick={handleCloseAddPackage} />
						<Button label="Add Package" type={ButtonType.Primary} onClick={handleAddPendingPackage} disabled={!canAdd} />
					</div>
				</>
			)}

			{currentPackages.length > 0 && !showPackageList && !pendingPackage && (
				<div className="current-packages">
					<ViewPackages
						selectedPackages={currentPackages}
						onRemovePackage={handleConfirmDelete}
						onUpdatePackageName={mode === SelectPackagesMode.Customer ? handleUpdatePackageName : undefined}
						altTitle="Currently Selected Packages"
						hidePrices={mode === SelectPackagesMode.Customer}
					/>
				</div>
			)}

			{confirmDelete && (
				<MessageModal
					title="Confirm Delete"
					type={MessageModalType.Question}
					onClick={handleRemovePackage}
					size="m"
				>
					This will remove the package. Are you sure you want to continue?
				</MessageModal>
			)}
		</div>
	);
};

export default SelectPackages;
